import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscription({ tier: 'guest', has_subscription: false });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      } else {
        // User not authenticated or error
        setSubscription({ tier: 'guest', has_subscription: false });
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.message);
      setSubscription({ tier: 'guest', has_subscription: false });
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Helper functions
  const isPremium = subscription?.tier === 'premium' || subscription?.has_subscription;
  const isFree = subscription?.tier === 'free' && !subscription?.has_subscription;
  const isGuest = !localStorage.getItem('token');

  const canUseFeature = (feature) => {
    if (isPremium) return true;
    
    // Free tier restrictions
    const freeFeatures = ['text', 'exam_presets'];
    return freeFeatures.includes(feature);
  };

  const getDailyQuizzesRemaining = () => {
    if (isPremium) return Infinity;
    const limit = subscription?.daily_quizzes_limit || 3;
    const used = subscription?.daily_quizzes_used || 0;
    return Math.max(0, limit - used);
  };

  const getMaxQuestions = () => {
    if (isPremium) return 20;
    return subscription?.features?.max_questions || 7;
  };

  const getTextCharLimit = () => {
    if (isPremium) return null;
    return subscription?.features?.text_char_limit || 5000;
  };

  const value = {
    subscription,
    loading,
    error,
    isPremium,
    isFree,
    isGuest,
    canUseFeature,
    getDailyQuizzesRemaining,
    getMaxQuestions,
    getTextCharLimit,
    refreshSubscription: fetchSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
